import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpErrorResponse } from '@angular/common/http';
import { QueryClient, QueryClientConfig } from '@tanstack/query-core';

import { InvalidateOnWriteInterceptor } from './invalidate-on-write.interceptor';

const MAX_QUERY_RETRIES = 1;

/** Retry once for network or server errors; a 4xx won't succeed on retry. */
function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500) return false;
  return failureCount < MAX_QUERY_RETRIES;
}

export function createQueryClient(config: QueryClientConfig = {}): QueryClient {
  return new QueryClient({
    ...config,
    defaultOptions: {
      ...config.defaultOptions,
      queries: {
        // Cached data is shown instantly; after 30s it's refetched in the background on next use.
        staleTime: 30_000,
        // Unused queries are kept for 5 minutes so navigating back is instant.
        gcTime: 5 * 60_000,
        retry: shouldRetryQuery,
        refetchOnWindowFocus: true,
        ...config.defaultOptions?.queries
      }
    }
  });
}

/**
 * Provides a single app-wide `QueryClient`, injectable by its class, and invalidates it after every
 * successful write request (see `InvalidateOnWriteInterceptor`). Import it where `HttpClientModule` is imported.
 *
 * The cache is in memory only. Don't add a persister: queries hold nurse PII and pay data.
 * The app must call `queryClient.clear()` on logout so the next user can't see cached data.
 */
@NgModule({})
export class QueryModule {
  static forRoot(config?: QueryClientConfig): ModuleWithProviders<QueryModule> {
    return {
      ngModule: QueryModule,
      providers: [
        { provide: QueryClient, useFactory: () => createQueryClient(config) },
        { provide: HTTP_INTERCEPTORS, useClass: InvalidateOnWriteInterceptor, multi: true },
        {
          // Subscribes the client to window focus and online events, for refetch-on-focus/reconnect.
          provide: APP_INITIALIZER,
          multi: true,
          useFactory: (client: QueryClient) => () => client.mount(),
          deps: [QueryClient]
        }
      ]
    };
  }
}
